import {
	createStore,
	createSubscriber,
	createContainer,
	createStateHook,
	createHook,
} from 'react-sweet-state';
import type { Action } from 'react-sweet-state';
import { graphql } from 'react-relay';
import type { GraphQLTaggedNode } from 'react-relay';

import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { type UseReadFragmentInteropResult } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import { getMultiMacroQueryBlocklistFF } from './useMultiMacroQuery.ts';
import type {
	multiMacroQueryQuery$data,
	multiMacroQueryQuery$variables,
} from './__generated__/multiMacroQueryQuery.graphql.ts';
import type { multiMacroQueryWithAppKeyQuery$data } from './__generated__/multiMacroQueryWithAppKeyQuery.graphql.ts';

// Relay query for macros without appKey - used by preloader and will be used by this store after migration
export const MultiMacroQueryQuery = graphql`
	query multiMacroQueryQuery(
		$contentId: ID!
		$mode: MacroRendererMode = RENDERER
		$blocklist: [String!]
		$first: Int
		$after: String
		$refetchToken: String
	) @allowHigherTimeout {
		macros(
			contentId: $contentId
			blocklist: $blocklist
			first: $first
			after: $after
			refetchToken: $refetchToken
		) {
			pageInfo {
				hasNextPage
				endCursor
			}
			nodes {
				macroId
				contentId
				renderedMacro(mode: $mode) {
					appKey
					value
					macroRenderedRepresentation
					macroBodyStorage
					webResource {
						contexts
						keys
						superbatch {
							metatags
						}
						tags {
							css
							data
							js
						}
						uris {
							css
							data
							js
						}
					}
					mediaToken {
						token
					}
				}
			}
		}
	}
`;

// Relay query for macros with appKey - used by preloader and will be used by this store after migration
export const MultiMacroQueryWithAppKeyQuery = graphql`
	query multiMacroQueryWithAppKeyQuery(
		$contentId: ID!
		$mode: MacroRendererMode = RENDERER
		$blocklist: [String!]
		$first: Int
		$after: String
		$refetchToken: String
	) @allowHigherTimeout {
		macros(
			contentId: $contentId
			blocklist: $blocklist
			first: $first
			after: $after
			refetchToken: $refetchToken
		) {
			pageInfo {
				hasNextPage
				endCursor
			}
			nodes {
				macroId
				contentId
				renderedMacro(mode: $mode) {
					appKey
					value
					macroRenderedRepresentation
					macroBodyStorage
					webResource {
						contexts
						keys
						superbatch {
							metatags
						}
						tags {
							css
							data
							js
						}
						uris {
							css
							data
							js
						}
					}
					mediaToken {
						token
					}
				}
			}
		}
	}
`;

type StoreState = {
	contentId?: string;
	featureFlags?: Record<string, any>;
	revision?: string;
	complete: boolean;
	data: any;
	cursor?: string;
	macroData: Record<string, any>; // macroId -> data
	error?: boolean;
	readFragmentInterop: UseReadFragmentInteropResult<any>;
};

type ContainerParams = {
	contentId: string;
	contentRendererMode: string;
	featureFlags: Record<string, any>;
	cursor?: string;
	revision?: string;
	useServerRenderedData?: boolean;
	readFragmentInterop: UseReadFragmentInteropResult<any>;
};

const DEFAULT_PRELOAD_PAGE_SIZE = 8;
const DEFAULT_PAGE_SIZE = 10;

const actions = {
	queryMacros:
		({
			contentId,
			contentRendererMode,
			featureFlags,
			cursor,
			revision,
			useServerRenderedData = false,
			readFragmentInterop,
		}): Action<StoreState> =>
		async ({ getState, setState, dispatch }) => {
			// avoid querying if already in progress
			if (
				getState().contentId === contentId &&
				getState().revision === revision &&
				getState().cursor === cursor
			) {
				return;
			}

			setState({
				contentId,
				revision,
				cursor,
				complete: false,
			});

			let mode = contentRendererMode.toUpperCase();
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			if (!process.env.REACT_SSR) {
				mode = window.location.pathname.startsWith('/wiki/pdf/') ? 'PDF' : mode;
			}

			const selectedQuery: GraphQLTaggedNode = MultiMacroQueryWithAppKeyQuery;

			const queryVariables: multiMacroQueryQuery$variables = {
				contentId,
				mode,
				blocklist: getMultiMacroQueryBlocklistFF(),
				first: !cursor ? DEFAULT_PRELOAD_PAGE_SIZE : DEFAULT_PAGE_SIZE,
				after: cursor,
			};

			// don't include revision if in SSR otherwise it will cause a cache miss between
			// SSR preload where revision is unknown and SSR render when this action is called
			if (!useServerRenderedData) {
				queryVariables.refetchToken = revision;
			}
			let data;
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			if (process.env.REACT_SSR && !getState().cursor) {
				data = readFragmentInterop({
					id: `$ROOT_QUERY.macros(${JSON.stringify({
						...(cursor ? { after: cursor } : {}),
						blocklist: queryVariables.blocklist,
						contentId,
						first: queryVariables.first,
						...(!useServerRenderedData ? { refetchToken: revision } : {}),
					})})`,
				});

				// have fragment take on same shape as query result
				if (data) {
					data = { macros: data } as any;
				}
			} else {
				const shouldUseServerRenderedData = useServerRenderedData && !getState().cursor;

				if (shouldUseServerRenderedData) {
					try {
						data = readFragmentInterop({
							id: `$ROOT_QUERY.macros(${JSON.stringify({
								...(cursor ? { after: cursor } : {}),
								blocklist: queryVariables.blocklist,
								contentId,
								first: queryVariables.first,
							})})`,
						});
						// have fragment take on same shape as query result
						if (data) {
							data = { macros: data } as any;
						}
					} catch (e) {
						//do not report error when query returns empty result in SSR
						if (e?.message && !e?.message.includes('Invariant Violation: 8')) {
							getMonitoringClient().submitError(e, {
								attribution: 'backbone',
							});
						}
					}
				}

				if (!data || !data?.macros || !shouldUseServerRenderedData) {
					const result = await queryInterop<
						multiMacroQueryQuery$data | multiMacroQueryWithAppKeyQuery$data,
						multiMacroQueryQuery$variables
					>({
						query: selectedQuery,
						variables: queryVariables,
						context: {
							allowOnExternalPage: true,
						},
					});

					if (result.error) {
						getMonitoringClient().submitError(result.error, {
							attribution: 'backbone',
						});

						setState({
							error: true,
						});
					}

					data = result.data;
				}
			}

			const { nodes = [], pageInfo = {} } = data?.macros;
			const macros = nodes?.reduce((nodeMap, node) => {
				if (node) {
					const key = getMacrosCacheKey(node.contentId || '', node.macroId || '');
					nodeMap[key] = node?.renderedMacro;
				}
				return nodeMap;
			}, {});

			setState({
				macroData: {
					...getState().macroData,
					...macros,
				},
			});

			if (pageInfo.hasNextPage) {
				void dispatch(
					actions.queryMacros({
						contentId,
						contentRendererMode,
						featureFlags,
						cursor: pageInfo.endCursor,
						revision,
						readFragmentInterop,
					}),
				);
			} else {
				setState({
					complete: true,
				});
			}
		},
	//only for testing
	resetState:
		(): Action<StoreState> =>
		({ setState }) => {
			setState({
				complete: false,
				data: {},
				macroData: {},
				error: false,
				contentId: undefined,
				revision: undefined,
				cursor: undefined,
			});
		},
};

const store = createStore<StoreState, typeof actions>({
	initialState: { complete: false, data: {}, macroData: {}, readFragmentInterop: () => null },
	actions,
	name: 'MultiMacroQueryStore',
});

export const MultiMacroQueryContainer = createContainer(store, {
	onInit:
		() =>
		(
			{ dispatch },
			{
				contentId,
				contentRendererMode,
				featureFlags,
				cursor,
				revision,
				useServerRenderedData,
				readFragmentInterop,
			}: ContainerParams,
		) => {
			void dispatch(
				actions.queryMacros({
					contentId,
					contentRendererMode,
					featureFlags,
					cursor,
					revision,
					useServerRenderedData,
					readFragmentInterop,
				}),
			);
		},
	onUpdate:
		() =>
		(
			{ dispatch },
			{
				contentId,
				contentRendererMode,
				featureFlags,
				cursor,
				revision,
				useServerRenderedData,
				readFragmentInterop,
			}: ContainerParams,
		) => {
			void dispatch(
				actions.queryMacros({
					contentId,
					contentRendererMode,
					featureFlags,
					cursor,
					revision,
					useServerRenderedData,
					readFragmentInterop,
				}),
			);
		},
});

function getMacrosCacheKey(contentId: string, macroId: string) {
	return `${contentId}::${macroId ?? 0}`;
}

function multiMacroQuerySelector(
	state: StoreState,
	params: { macroId: string; contentId: string },
) {
	const key = getMacrosCacheKey(params.contentId, params.macroId);
	const macroData = state.macroData?.[key];

	return {
		loading: !Boolean(macroData) && !state.complete,
		error: !Boolean(macroData) && state.error,
		renderedMacro: macroData || null,
		complete: state.complete,
	};
}

export const useMultiMacroQuery = createStateHook(store, {
	selector: multiMacroQuerySelector,
});

export const MultiMacroQuerySubscriber = createSubscriber(store, {
	selector: multiMacroQuerySelector,
});

// exported for testing store actions
export const useMultiMacroQueryStoreHook = createHook(store);
